import {
  DollarOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  MailOutlined,
  NumberOutlined,
  PercentageOutlined,
  PhoneOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { MyLogo, SharedSwitch, ShareGarageModal, VaultButton } from '@components';
import { isGranted } from '@lib';
import { ProfileLogoInput, TenantProfile } from '@services';
import { useStore } from '@stores';
import { Button, Descriptions, Divider, Rate, Space, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { GarageVatRegistration } from '../Details/GarageVatRegistration/GarageVatRegistration.container';

const { Text } = Typography;

// --- Shared styles (matches CustomerDetailsCard) ---

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 14,
  color,
  marginTop: 2,
  flexShrink: 0,
});

const emptyStyle: React.CSSProperties = {
  wordBreak: 'break-word',
  fontSize: 14,
  lineHeight: '1.4',
  color: 'rgba(0, 0, 0, 0.45)',
};

const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };

const Label = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <span style={labelStyle}>
    {icon} {text}
  </span>
);

// --- Field definitions ---

interface FieldDef {
  icon: React.ReactNode;
  label: string;
  color: string;
  value?: string | null;
  fallback: string;
}

// 2-column, 3-row layout (interleaved for left-to-right fill):
// Row 1: Email      | VAT
// Row 2: Phone      | VAT Number
// Row 3: Website    | Labour rate
const getGarageFields = (tenantProfile: TenantProfile): FieldDef[] => [
  {
    icon: <MailOutlined />,
    label: 'Email',
    color: '#1677ff',
    value: tenantProfile.companyEmail,
    fallback: 'No email provided',
  },
  {
    icon: <PercentageOutlined />,
    label: 'VAT',
    color: '#eb2f96',
    value: tenantProfile.vat != null ? `${tenantProfile.vat}%` : undefined,
    fallback: 'No VAT provided',
  },
  {
    icon: <PhoneOutlined />,
    label: 'Phone',
    color: '#52c41a',
    value: tenantProfile.companyPhone,
    fallback: 'No phone provided',
  },
  {
    icon: <NumberOutlined />,
    label: 'VAT Number',
    color: '#722ed1',
    value: tenantProfile.vatNumber,
    fallback: 'Not registered',
  },
  {
    icon: <GlobalOutlined />,
    label: 'Website',
    color: '#13c2c2',
    value: tenantProfile.website?.toString(),
    fallback: 'No website provided',
  },
  {
    icon: <DollarOutlined />,
    label: 'Labour rate excl. VAT',
    color: '#fa8c16',
    value: tenantProfile.labourRate?.toString(),
    fallback: 'No rate provided',
  },
];

// --- Main component ---

interface IProps {
  tenantProfile: TenantProfile;
  handleOnlineBookingChange?: () => void;
  hideVaultButton?: boolean;
}

export const GarageDetailsCard: React.FC<IProps> = observer(
  ({ tenantProfile, handleOnlineBookingChange, hideVaultButton = false }) => {
    const { tenantProfileStore } = useStore();
    const [shareVisible, setShareVisible] = useState(false);

    const handleLogoUpdate = useCallback(
      async (profileLogo: ProfileLogoInput) => await tenantProfileStore.updateLogo(profileLogo),
      [tenantProfileStore]
    );

    const handleLogoDelete = useCallback(
      async () => await tenantProfileStore.deleteLogo(),
      [tenantProfileStore]
    );

    const handleActiveChange = useCallback(
      async (id: number) => await tenantProfileStore.changeActive(id),
      [tenantProfileStore]
    );

    const fields = getGarageFields(tenantProfile);

    return (
      <>
        {/* Header: Logo + Name/Address + Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <MyLogo
            disabled={!isGranted('Pages.MyGarage.Manager')}
            url={tenantProfile.logo}
            handleLogoDelete={handleLogoDelete}
            handleLogoUpdate={handleLogoUpdate}
            enableCrop={false}
          />

          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 20, color: 'rgba(0, 0, 0, 0.85)' }}>
                {tenantProfile.tenant?.name}
              </strong>

              {isGranted('Pages.MyGarage.Manager') && tenantProfile.onlineBooking && (
                <Tag color="success">Online booking</Tag>
              )}

              {isGranted('Pages.Tenants') && (
                <Tag color={tenantProfile.tenant?.isActive ? 'green' : 'red'}>
                  {tenantProfile.tenant?.isActive ? 'Active' : 'Inactive'}
                </Tag>
              )}

              {!isGranted('Pages.Tenants') && (
                <Button
                  type="text"
                  size="small"
                  onClick={() => setShareVisible(true)}
                  icon={<ShareAltOutlined style={{ color: '#2CBDFF' }} />}
                  style={{ border: 'none' }}
                />
              )}
            </div>

            {tenantProfile.rate != null && tenantProfile.rate > 0 && (
              <Space size={6} align="center" style={{ marginTop: 4 }}>
                <Rate
                  disabled
                  allowHalf
                  value={tenantProfile.rate}
                  style={{ fontSize: 14, color: '#faad14' }}
                />
                <Text style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.65)' }}>
                  {tenantProfile.rate.toFixed(1)}
                  {tenantProfile.rateCount != null && ` (${tenantProfile.rateCount})`}
                </Text>
              </Space>
            )}

            {(tenantProfile.address || tenantProfile.city) && (
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 13, marginTop: 4, display: 'block' }}>
                <EnvironmentOutlined style={{ marginRight: 4 }} />
                {[tenantProfile.address, tenantProfile.zipCode, tenantProfile.city].filter(Boolean).join(', ')}
              </Text>
            )}

            {/* Row 1: Switches + VAT reg */}
            <Space size="middle" wrap style={{ marginTop: 8 }}>
              {isGranted('Pages.MyGarage.Manager') && (
                <Space size="small" align="center">
                  <Text style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.65)' }}>Online booking</Text>
                  <SharedSwitch
                    active={tenantProfile.onlineBooking}
                    onChange={handleOnlineBookingChange}
                  />
                </Space>
              )}
              {isGranted('Pages.Tenants') && (
                <Space size="small" align="center">
                  <Text style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.65)' }}>Active</Text>
                  <SharedSwitch
                    active={tenantProfile.tenant?.isActive}
                    onClick={() => handleActiveChange(tenantProfile.tenantId)}
                  />
                </Space>
              )}
              {isGranted('Pages.MyGarage.Manager') &&
                tenantProfile?.vat != null &&
                tenantProfile.vat > 0 && (
                  <GarageVatRegistration vatNumber={tenantProfile.vatNumber} />
                )}
            </Space>

          </div>
        </div>

        {/* Buttons row */}
        <Space size="small" wrap style={{ marginTop: 12 }}>
          {!hideVaultButton && <VaultButton tenantId={tenantProfile.tenant?.id!} />}
          <Link to={`/tenant-concerns/${tenantProfile.tenantId}`}>
            <Button type="primary">View concerns</Button>
          </Link>
        </Space>

        <Divider style={{ margin: '16px 0' }} />

        {/* Detail fields - Descriptions table */}
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
          bordered
          styles={{ label: { fontWeight: 'bold' } }}
        >
          {fields.map((f, i) => (
            <Descriptions.Item
              key={i}
              label={
                <Label
                  icon={React.cloneElement(f.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
                    style: iconStyle(f.color),
                  })}
                  text={f.label}
                />
              }
            >
              {f.value ? f.value : <span style={emptyStyle}>{f.fallback}</span>}
            </Descriptions.Item>
          ))}
        </Descriptions>

        {/* Share modal */}
        <ShareGarageModal
          visible={shareVisible}
          setVisible={setShareVisible}
          companyLogo={tenantProfile.logo}
        />
      </>
    );
  }
);
